import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { CountryData } from 'app/models/dto/country-data.model';
import { CountryDataService } from 'app/services/country/country-data.service';
import { catchError, tap } from 'rxjs/operators';
import { GetCountryData, SelectCountry } from './country-data.actions';

export class CountryDataStateModel {
  countryData: CountryData[];
  selectedCountry: CountryData | null;
  loading: boolean;
}

@State<CountryDataStateModel>({
  name: 'countryData',
  defaults: {
    countryData: [],
    selectedCountry: null,
    loading: false
  }
})

@Injectable()

export class CountryDataState {

  @Selector()
  static countryData(state: CountryDataStateModel) {
    return state?.countryData;
  }

  @Selector()
  static selectedCountry(state: CountryDataStateModel) {
    return state.selectedCountry;
  }

  @Selector()
  static zonesByCountry(state: CountryDataStateModel) {
    return (country: string) => {
      const searchedCountry = state.countryData.find(cd => cd.countryCode === country);
      if (!searchedCountry) {
        return null;
      }
      return searchedCountry.zones;
    };
  }

  @Selector()
  static phoneCountryCodes(state: CountryDataStateModel) {
    return state.countryData.map(cd => ({
        id: cd.phone_country_code.id,
        name: cd.name,
        phoneCountryCode: cd.phone_country_code.countryCode,
        countryCode: cd.countryCode
      }));
  }

  @Selector()
  static countryFromPhoneCountyCodeId(state: CountryDataStateModel) {
    return (phoneCountryCodeId: string) => {
      if (!phoneCountryCodeId) {
        return null;
      }
      const countryData = state.countryData.find(country => country.phone_country_code.id === parseInt(phoneCountryCodeId));
      return countryData ? countryData : null;
    };
  }

  @Selector()
  static countries(state: CountryDataStateModel) {
    return state.countryData.map(cd => ({
        id: cd.id,
        name: cd.name,
        countryCode: cd.countryCode,
      }));
  }

  @Selector()
  static countryCodeFromPhoneCountryCode(state: CountryDataStateModel) {
    return (countryCodeId: string) => {
      const countryCodeData = countryCodeId
        ? state.countryData.find(country => country.phone_country_code.countryCode === parseInt(countryCodeId))
        : null;

      return countryCodeData ? countryCodeData : null;
    };
  }

  @Selector()
  static countryByZone(state: CountryDataStateModel) {
    return (zone: string) => {
      let foundCountry: {} | null = null;
      state.countryData.forEach(cd => {
        if (cd.zones.some(z => z.name === zone || z.ISO3166Code === zone)) {
          foundCountry = {
            id: cd.id,
            name: cd.name,
            countryCode: cd.countryCode,
          };
        }
      });
      return foundCountry;
    };
  }

  constructor(private countryDataService: CountryDataService) {}

  @Action(GetCountryData)
  getCountryData(
    { getState, patchState }: StateContext<CountryDataStateModel>,
    { }: GetCountryData
  ) {
    if (getState().countryData.length || getState().loading) {
      return;
    }
    patchState({ loading: true });
    return this.countryDataService.getCountryData()
      .pipe(
        catchError(err => {
          patchState({ loading: false });
          throw err;
        }),
        tap(countryData => {
          patchState({ countryData, loading: false });
        })
      );
  }

  @Action(SelectCountry)
  selectCountry(
    { getState, patchState }: StateContext<CountryDataStateModel>,
    { countryId }: SelectCountry
  ) {
    const country = getState().countryData.find(x => x.id === countryId);
    patchState({ selectedCountry: country });
  }

}
